import axios from "axios";
import { baseUrl } from "../util";

const getConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    "Content-Type": "application/json",
  },
});

export const educationService = {
  // Category endpoints
  getCategories: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/categories/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching education categories:", error);
      throw error;
    }
  },

  // Keywords/Tags endpoints
  getKeywords: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/keywords/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching education keywords:", error);
      throw error;
    }
  },

  // Course endpoints
  getCourses: async (filters = {}) => {
    try {
      let queryString = "";
      if (Object.keys(filters).length > 0) {
        queryString = "?" + new URLSearchParams(filters).toString();
      }

      const response = await axios.get(
        `${baseUrl}/api/edu/courses/${queryString}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching courses:", error);
      throw error;
    }
  },

  getCourseById: async (courseId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/courses/${courseId}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching course ${courseId}:`, error);
      throw error;
    }
  },

  getFeaturedCourses: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/courses/featured/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching featured courses:", error);
      throw error;
    }
  },

  getCoursesByCategory: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/courses/by_category/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching courses by category:", error);
      throw error;
    }
  },

  // Class endpoints
  getClassesByCourse: async (courseId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/classes/?course=${courseId}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching classes for course ${courseId}:`, error);
      throw error;
    }
  },

  getClassById: async (classId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/classes/${classId}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching class ${classId}:`, error);
      throw error;
    }
  },

  // Content endpoints
  getContentsByClass: async (classId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/contents/?edu_class=${classId}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching contents for class ${classId}:`, error);
      throw error;
    }
  },

  getContentById: async (contentId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/contents/${contentId}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching content ${contentId}:`, error);
      throw error;
    }
  },

  // My Courses endpoints (enrolled courses)
  getMyCourses: async (filters = {}) => {
    try {
      let queryString = "";
      if (Object.keys(filters).length > 0) {
        queryString = "?" + new URLSearchParams(filters).toString();
      }

      const response = await axios.get(
        `${baseUrl}/api/edu/my-courses/${queryString}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user enrolled courses:", error);
      throw error;
    }
  },

  getMyCourseById: async (myCourseId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/my-courses/${myCourseId}/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching enrolled course ${myCourseId}:`, error);
      throw error;
    }
  },

  enrollInCourse: async (courseId) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/my-courses/`,
        { course: courseId },
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error enrolling in course ${courseId}:`, error);
      throw error;
    }
  },

  unenrollFromCourse: async (myCourseId) => {
    try {
      await axios.delete(
        `${baseUrl}/api/edu/my-courses/${myCourseId}/`,
        getConfig()
      );
      return true;
    } catch (error) {
      console.error(`Error unenrolling from course ${myCourseId}:`, error);
      throw error;
    }
  },

  toggleFavoriteCourse: async (myCourseId) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/my-courses/${myCourseId}/toggle_favorite/`,
        {},
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error toggling favorite for course ${myCourseId}:`, error);
      throw error;
    }
  },

  getFavoriteCourses: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/my-courses/favorites/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching favorite courses:", error);
      throw error;
    }
  },

  getInProgressCourses: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/my-courses/in_progress/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching in-progress courses:", error);
      throw error;
    }
  },

  getCompletedCourses: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/my-courses/completed/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching completed courses:", error);
      throw error;
    }
  },

  // Class Progress endpoints
  getClassProgress: async (filters = {}) => {
    try {
      let queryString = "";
      if (Object.keys(filters).length > 0) {
        queryString = "?" + new URLSearchParams(filters).toString();
      }

      const response = await axios.get(
        `${baseUrl}/api/edu/class-progress/${queryString}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching class progress:", error);
      throw error;
    }
  },
  createClassProgress: async (progressData) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/class-progress/`,
        progressData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating class progress:", error);
      throw error;
    }
  },

  updateClassProgressStatus: async (progressId, status) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/class-progress/${progressId}/update_status/`,
        { status },
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating class progress ${progressId}:`, error);
      throw error;
    }
  },

  toggleFavoriteClass: async (progressId) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/class-progress/${progressId}/toggle_favorite/`,
        {},
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error toggling favorite for class ${progressId}:`, error);
      throw error;
    }
  },

  // Content Progress endpoints
  getContentProgress: async (filters = {}) => {
    try {
      let queryString = "";
      if (Object.keys(filters).length > 0) {
        queryString = "?" + new URLSearchParams(filters).toString();
      }

      const response = await axios.get(
        `${baseUrl}/api/edu/content-progress/${queryString}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching content progress:", error);
      throw error;
    }
  },

  createContentProgress: async (progressData) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/content-progress/`,
        progressData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating content progress:", error);
      throw error;
    }
  },

  updateContentPosition: async (progressId, position) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/content-progress/${progressId}/update_position/`,
        { position },
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating content position for ${progressId}:`,
        error
      );
      throw error;
    }
  },

  markContentCompleted: async (progressId) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/content-progress/${progressId}/mark_completed/`,
        {},
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error marking content completed for ${progressId}:`,
        error
      );
      throw error;
    }
  },

  // Submit quiz results
  submitQuizResult: async (quizData) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/video-quiz-results/submit_quiz/`,
        quizData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error submitting quiz result:", error);
      throw error;
    }
  },

  // Bookmark endpoints
  getBookmarks: async (filters = {}) => {
    try {
      let queryString = "";
      if (Object.keys(filters).length > 0) {
        queryString = "?" + new URLSearchParams(filters).toString();
      }

      const response = await axios.get(
        `${baseUrl}/api/edu/bookmarks/${queryString}`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching bookmarks:", error);
      throw error;
    }
  },

  createBookmark: async (bookmarkData) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/edu/bookmarks/`,
        bookmarkData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error creating bookmark:", error);
      throw error;
    }
  },

  updateBookmark: async (bookmarkId, bookmarkData) => {
    try {
      const response = await axios.patch(
        `${baseUrl}/api/edu/bookmarks/${bookmarkId}/`,
        bookmarkData,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating bookmark ${bookmarkId}:`, error);
      throw error;
    }
  },

  deleteBookmark: async (bookmarkId) => {
    try {
      await axios.delete(
        `${baseUrl}/api/edu/bookmarks/${bookmarkId}/`,
        getConfig()
      );
      return true;
    } catch (error) {
      console.error(`Error deleting bookmark ${bookmarkId}:`, error);
      throw error;
    }
  },
  getFavoriteClasses: async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/edu/class-progress/favorites/`,
        getConfig()
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching favorite classes:", error);
      throw error;
    }
  },
};
